import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  ResponsiveContainer, ScatterChart, Scatter, XAxis, YAxis, ZAxis,
  CartesianGrid, Tooltip, ReferenceLine, ReferenceArea,
} from 'recharts';
import api from '../../services/api';
import './SectorHeatmapApp.css';

// One colour per sector, constant across every region tab so a sector is
// recognisable wherever it appears. Hue families follow the ICB hierarchy: the
// level-2 supersectors are shades of their level-1 parent.
const SECTOR_COLOURS = {
  TECNO: '#072040',
  TELCM: '#4a6fa5',
  HLTHC: '#2CA6A4',
  FINAN: '#f0a500', BANKS: '#c97f00', FINSV: '#ffc94d', INSUR: '#8a6d1f',
  RLEST: '#7E5BA6',
  CODIS: '#C2649A', AUTMB: '#8f3f6b', CONPS: '#e08cb8',
  MEDIA: '#a34d80', RTAIL: '#d9a3c4', TRLES: '#6d2d4f',
  COSTP: '#91CF9B', FDBEV: '#4c8a58', PCDGS: '#bfe3c5',
  INDUS: '#599BE3', CNSTM: '#2f6fa8', INDGS: '#9cc5ee',
  BMATR: '#8C4A4A', BRESR: '#5e2f2f', CHMCL: '#c08585',
  ENEGY: '#212529',
  UTILS: '#6c757d',
  TOTMK: '#072040',
};

// For tabs where colour carries the region instead. World is deliberately
// neutral: on the Countries tab it is an aggregate of the others, not a peer.
const REGION_COLOURS = {
  US: '#072040', EU: '#f0a500', UK: '#599BE3', JP: '#8C4A4A',
  AU: '#91CF9B', CA: '#7E5BA6', EK: '#2CA6A4', WD: '#6c757d',
};

const REGION_ORDER = ['WD', 'US', 'EU', 'UK', 'JP', 'AU', 'CA', 'EK'];
const CTRY_TAB = 'CTRY';
const ALL_TAB = 'ALL';

const Y_METRICS = [
  ['Summary', [
    ['momentum_score', 'Earnings momentum (average)'],
  ]],
  ['Trailing (realised)', [
    ['g6m', 'Trailing EPS growth, 6m'],
    ['g1y', 'Trailing EPS growth, 1y'],
    ['g3y', 'Trailing EPS growth, 3y p.a.'],
  ]],
  ['Forward (estimate revisions)', [
    ['f6m', 'Forward EPS revision, 6m'],
    ['f1y', 'Forward EPS revision, 1y'],
    ['f3y', 'Forward EPS revision, 3y p.a.'],
  ]],
  ['Expected', [
    ['implied_growth', 'Implied next-12m growth'],
  ]],
];
const Y_LABEL = Object.fromEntries(Y_METRICS.flatMap(([, opts]) => opts));
// The summary metric is a 0-100 rank, not a growth rate, so the axis, the
// quadrant and the tick format all switch with it.
const isScore = (m) => m === 'momentum_score';

const pct = (v, dp = 1) => (v == null ? '–' : `${(v * 100).toFixed(dp)}%`);
const num = (v, dp = 1) => (v == null ? '–' : v.toFixed(dp));
const int = (v) => (v == null ? '–' : v.toFixed(0));

const colourOf = (r, mode) =>
  (mode === 'region' ? REGION_COLOURS[r.region] : SECTOR_COLOURS[r.code]) || '#6c757d';
const groupKey = (r, mode) => (mode === 'region' ? r.region : r.code);
const groupName = (r, mode) => (mode === 'region' ? r.regionName : r.sector);
// A whole-market row's sector_name is 'Whole market'; the entity it names is
// the country, so that is the label everywhere it is displayed.
const label = (r) => (r.level === 0 ? r.regionName : r.sector);

// Level 0 (whole market) and 1 (industry) are filled; level 2 supersectors are
// smaller and faded, because they are subsets of their parent industry.
function PointShape(props) {
  const { cx, cy, fill, payload, dimmed, focused } = props;
  if (cx == null || cy == null) return null;
  const l2 = payload.level === 2;
  const base = l2 ? 4.5 : 7;
  return (
    <circle
      cx={cx} cy={cy} r={focused ? base + 2.5 : base}
      fill={fill} fillOpacity={l2 ? 0.5 : 0.9}
      stroke={payload.opp ? '#1d5c2c' : '#072040'}
      strokeWidth={payload.opp ? 2.4 : 0.9}
      opacity={dimmed ? 0.15 : 1}
      style={{ cursor: 'pointer' }}
    />
  );
}

// Defined at module scope, not inside the tooltip: a component declared in a
// render body is a new type on every render, which remounts its subtree.
function Row({ k, v }) {
  return <div className="sh-tip-row"><span>{k}</span><b>{v}</b></div>;
}

function PointTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const r = payload[0].payload;
  const kind = r.level === 0 ? 'whole market' : (r.level === 2 ? 'supersector' : 'industry');
  return (
    <div className="sh-tip">
      <span className="sh-tip-head">{label(r)}</span>
      <Row k={r.level === 0 ? kind : `${r.regionName} · ${kind}`} v={r.ticker} />
      <Row k="Trailing 6m / 1y" v={`${pct(r.g6m)} / ${pct(r.g1y)}`} />
      <Row k="Trailing 3y p.a." v={pct(r.g3y)} />
      <Row k="Forward 6m / 1y" v={`${pct(r.f6m)} / ${pct(r.f1y)}`} />
      <Row k="Forward 3y p.a." v={pct(r.f3y)} />
      <Row k="Implied next 12m" v={pct(r.implied_growth)} />
      <Row k="Forward PE" v={`${num(r.pe_forward)}${r.fwd_pe_median == null ? '' : ` vs ${num(r.fwd_pe_median)} med`}`} />
      <Row k="Percentile" v={`${int(r.fwd_pe_pctile)}th of ${int(r.hist_years)}y`} />
      <Row k="Trailing PE pctile" v={int(r.trail_pe_pctile)} />
      <Row k="Price return 1y" v={pct(r.price_return_1y)} />
      <Row k="Momentum" v={`${int(r.momentum_score)} (trail ${int(r.mom_trail_score)} / fwd ${int(r.mom_fwd_score)})`} />
      <Row k="Value / Score" v={`${int(r.value_score)}  /  ${int(r.composite)}`} />
      {r.opp && <span className="sh-tip-tag">&#9679; clears both hurdles</span>}
    </div>
  );
}

const TABLE_COLS = [
  ['sector', 'Sector', 'text'],
  ['g6m', 'T 6m', 'pct'], ['g1y', 'T 1y', 'pct'], ['g3y', 'T 3y', 'pct'],
  ['f6m', 'F 6m', 'pct'], ['f1y', 'F 1y', 'pct'], ['f3y', 'F 3y', 'pct'],
  ['implied_growth', 'Impl 12m', 'pct'],
  ['pe_forward', 'Fwd PE', 'num'],
  ['fwd_pe_pctile', 'Pctile', 'pctile'],
  ['price_return_1y', '1y ret', 'pct'],
  ['momentum_score', 'Mom', 'int'],
  ['value_score', 'Val', 'int'],
  ['composite', 'Score', 'score'],
];

const COL_HELP = {
  g6m: 'Trailing EPS growth over 6 months',
  g1y: 'Trailing EPS growth over 1 year',
  g3y: 'Trailing EPS growth, 3 years annualised',
  f6m: 'Change in the 12m forward EPS estimate over 6 months (revisions)',
  f1y: 'Change in the 12m forward EPS estimate over 1 year (revisions)',
  f3y: 'Change in the 12m forward EPS estimate, 3 years annualised',
  implied_growth: 'Growth the market expects over the next 12m (forward EPS / trailing EPS)',
  pe_forward: '12m forward PE',
  fwd_pe_pctile: 'Forward PE percentile within its own history (0 = cheapest)',
  price_return_1y: 'Price return over 1 year, local currency',
  momentum_score: 'Momentum score: trailing and forward ranks, equally weighted',
  value_score: 'Value score: 100 minus the forward PE percentile',
  composite: 'Composite: average of momentum and value',
};

// Diverging scale for growth: maroon (negative) through white to green.
const growthBg = (v) => {
  if (v == null) return undefined;
  const t = Math.max(-1, Math.min(1, v / 0.4));
  return t >= 0
    ? `rgba(145,207,155,${(t * 0.75).toFixed(3)})`
    : `rgba(140,74,74,${(-t * 0.55).toFixed(3)})`;
};
// Valuation percentile: cheap green through to dear maroon.
const pctileBg = (v) => {
  if (v == null) return undefined;
  const t = v / 100;
  return t <= 0.5
    ? `rgba(145,207,155,${((0.5 - t) * 1.5).toFixed(3)})`
    : `rgba(140,74,74,${((t - 0.5) * 1.1).toFixed(3)})`;
};

export default function SectorHeatmapApp() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState(CTRY_TAB);
  const [yMetric, setYMetric] = useState('momentum_score');
  const [showL2, setShowL2] = useState(false);
  const [hoverGroup, setHoverGroup] = useState(null);
  const [sort, setSort] = useState({ key: 'composite', asc: false });

  const load = useCallback(() => {
    setLoading(true); setError(null);
    api.get('/api/sector-heatmap/report')
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const tabs = useMemo(() => {
    if (!data) return [];
    const rows = data.rows || [];
    const out = [{ code: CTRY_TAB, name: 'Countries', mode: 'region', showRegion: false,
                   rows: rows.filter((r) => r.level === 0) }];
    REGION_ORDER.forEach((rc) => {
      const sub = rows.filter((r) => r.region === rc && r.level >= 1);
      if (sub.length) {
        out.push({ code: rc, name: sub[0].regionName, mode: 'sector',
                   showRegion: false, rows: sub });
      }
    });
    out.push({
      code: ALL_TAB, name: 'All regions (ex World)', mode: 'region', showRegion: true,
      // World is an aggregate of the others rather than a peer, so it is excluded.
      rows: rows.filter((r) => r.level >= 1 && r.region !== 'WD'),
    });
    return out;
  }, [data]);

  const active = tabs.find((t) => t.code === tab) || tabs[0];
  // Level 0 is never a subset of anything, so it always shows.
  const rows = useMemo(
    () => (active?.rows || []).filter((r) => showL2 || r.level !== 2),
    [active, showL2],
  );

  const plotted = useMemo(
    () => rows.filter((r) => r.fwd_pe_pctile != null && r[yMetric] != null),
    [rows, yMetric],
  );

  const groups = useMemo(() => {
    const m = new Map();
    plotted.forEach((r) => {
      const k = groupKey(r, active.mode);
      if (!m.has(k)) m.set(k, { key: k, name: groupName(r, active.mode), rows: [], opp: 0,
                                colour: colourOf(r, active.mode), level: r.level });
      const g = m.get(k); g.rows.push(r); if (r.opp) g.opp += 1;
    });
    return [...m.values()].sort((a, b) => a.level - b.level || b.rows.length - a.rows.length);
  }, [plotted, active]);

  const yMedian = useMemo(() => {
    const ys = plotted.map((r) => r[yMetric]).sort((a, b) => a - b);
    return ys.length ? ys[Math.floor(ys.length / 2)] : 0;
  }, [plotted, yMetric]);

  const opportunities = useMemo(
    () => rows.filter((r) => r.opp).sort((a, b) => b.composite - a.composite).slice(0, 4),
    [rows],
  );

  const sorted = useMemo(() => {
    const s = [...rows];
    s.sort((a, b) => {
      const x = sort.key === 'sector' ? label(a) : a[sort.key];
      const y = sort.key === 'sector' ? label(b) : b[sort.key];
      if (x == null) return 1;
      if (y == null) return -1;
      const d = typeof x === 'string' ? x.localeCompare(y) : x - y;
      return sort.asc ? d : -d;
    });
    return s;
  }, [rows, sort]);

  if (loading) return <div className="sh-wrap"><p className="sh-muted">Loading…</p></div>;
  if (error) {
    return (
      <div className="sh-wrap">
        <div className="sh-alert">
          <strong>Could not load the sector heatmap.</strong> {error}
        </div>
      </div>
    );
  }

  const cutoff = data?.opportunityCutoff ?? 60;

  return (
    <div className="sh-wrap">
      <div className="sh-head">
        <h1>Sector Heatmap</h1>
        <p className="sh-muted">
          Datastream sector indices &middot; 25 sectors across 8 regions &middot; data as at {data?.as_of}
        </p>
        <p className="sh-method">
          <b>Momentum</b> has two halves, weighted equally: <b>trailing</b> EPS growth
          (index level &divide; trailing PE) and <b>forward</b> revisions (how the 12m estimate is
          being marked), each over 6m, 1y and 3y annualised, in local currency so no FX
          translation is embedded. <b>Valuation</b> is the 12m forward PE as a percentile of
          that sector&apos;s <i>own</i> monthly history back to 2000 &mdash; 0 means the cheapest it has
          ever been, not cheap relative to other sectors. <b>Score</b> averages the momentum rank
          (within region and ICB level) with a value score of 100 &minus; percentile. Sectors
          clearing {cutoff} on both are marked &#9679;.
        </p>
      </div>

      <div className="sh-tabs">
        {tabs.map((t) => (
          <button key={t.code} className={`sh-tab${t.code === active?.code ? ' on' : ''}`}
                  onClick={() => setTab(t.code)}>{t.name}</button>
        ))}
      </div>

      <div className="sh-toolbar">
        <label className="sh-field">
          <span>Vertical axis</span>
          <select value={yMetric} onChange={(e) => setYMetric(e.target.value)}>
            {Y_METRICS.map(([grp, opts]) => (
              <optgroup key={grp} label={grp}>
                {opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </optgroup>
            ))}
          </select>
        </label>
        <label className="sh-toggle">
          <input type="checkbox" checked={showL2} onChange={(e) => setShowL2(e.target.checked)} />
          show level-2 supersectors (subsets of their industry, ranked separately)
        </label>
        <button className="sh-btn" onClick={load}>Refresh</button>
      </div>

      <div className="sh-cards">
        {opportunities.length ? opportunities.map((r) => (
          <div className="sh-card" key={r.ticker}>
            <div className="sh-card-name">
              {label(r)}
              {active.mode === 'region' && r.level !== 0 && (
                <span className="sh-card-region"> {r.regionName}</span>
              )}
            </div>
            <div className="sh-card-meta">
              Trailing <b>{pct(r.g6m)}</b> 6m &middot; <b>{pct(r.g1y)}</b> 1y &middot; <b>{pct(r.g3y)}</b> 3y p.a.<br />
              Forward <b>{pct(r.f6m)}</b> 6m &middot; <b>{pct(r.f1y)}</b> 1y &middot; implied <b>{pct(r.implied_growth)}</b><br />
              Fwd PE <b>{num(r.pe_forward)}</b>, <b>{int(r.fwd_pe_pctile)}th</b> pctile of {int(r.hist_years)}y<br />
              Score <b>{int(r.composite)}</b> (mom {int(r.momentum_score)} / val {int(r.value_score)})
            </div>
          </div>
        )) : (
          <p className="sh-none">
            No sector clears both hurdles (momentum and value each &ge; {cutoff}) here.
          </p>
        )}
      </div>

      <div className="sh-chartwrap">
        <div className="sh-plot">
          <ResponsiveContainer width="100%" height={400}>
            <ScatterChart margin={{ top: 16, right: 20, bottom: 34, left: 8 }}>
              <CartesianGrid stroke="#eef1f5" />
              {/* Corner quadrants. On the summary metric both axes are scores,
                  so the green corner is exactly the opportunity rule (momentum
                  >= cutoff AND valuation percentile <= 100 - cutoff) and the red
                  corner is its mirror image. On a growth metric there is no such
                  threshold, so both fall back to the cross-sectional median. */}
              {/* Each corner leaves its open-ended bound UNSET so Recharts runs it
                  to the axis edge. Passing 'dataMin'/'dataMax' here does not work
                  - those are axis-domain keywords, not area coordinates, so
                  isNumOrStr() accepts them, scale.apply() returns NaN and the
                  area silently fails to render. ifOverflow="hidden" clips rather
                  than the default "discard", which drops the whole area if a
                  corner lands outside the range. */}
              <ReferenceArea
                x1={0} x2={isScore(yMetric) ? 100 - cutoff : 50}
                y1={isScore(yMetric) ? cutoff : yMedian}
                fill="#91CF9B" fillOpacity={0.14} ifOverflow="hidden"
                label={{ value: 'cheap + strong momentum', position: 'insideTopLeft',
                         fill: '#3f7a4c', fontSize: 10.5, fontWeight: 600 }} />
              <ReferenceArea
                x1={isScore(yMetric) ? cutoff : 50} x2={100}
                y2={isScore(yMetric) ? 100 - cutoff : yMedian}
                fill="#8C4A4A" fillOpacity={0.12} ifOverflow="hidden"
                label={{ value: 'expensive + weak momentum', position: 'insideBottomRight',
                         fill: '#8C4A4A', fontSize: 10.5, fontWeight: 600 }} />
              <XAxis type="number" dataKey="fwd_pe_pctile" domain={[0, 100]}
                     ticks={[0, 25, 50, 75, 100]} stroke="#072040" fontSize={11}
                     label={{ value: isScore(yMetric)
                                ? `Forward PE percentile vs own history (0 = cheapest; <= ${100 - cutoff} clears the value hurdle)`
                                : 'Forward PE percentile vs own history (0 = cheapest)',
                              position: 'insideBottom', offset: -18,
                              fill: '#072040', fontSize: 11.5 }} />
              <YAxis type="number" dataKey={yMetric} stroke="#072040" fontSize={11}
                     domain={isScore(yMetric) ? [0, 100] : ['auto', 'auto']}
                     tickFormatter={(v) => (isScore(yMetric)
                       ? v.toFixed(0) : `${(v * 100).toFixed(0)}%`)}
                     label={{ value: Y_LABEL[yMetric], angle: -90, position: 'insideLeft',
                              fill: '#072040', fontSize: 11.5, style: { textAnchor: 'middle' } }} />
              <ZAxis range={[60, 60]} />
              <ReferenceLine x={isScore(yMetric) ? 100 - cutoff : 50}
                             stroke="#6c757d" strokeDasharray="3 3" />
              <ReferenceLine y={isScore(yMetric) ? cutoff : 0}
                             stroke="#6c757d" strokeDasharray="3 3" />
              <Tooltip content={<PointTooltip />} cursor={{ strokeDasharray: '3 3' }} />
              {/* level-2 first so the industries sit on top */}
              {[...groups].sort((a, b) => b.level - a.level).map((g) => (
                <Scatter
                  key={g.key} data={g.rows} fill={g.colour} isAnimationActive={false}
                  onMouseEnter={() => setHoverGroup(g.key)}
                  onMouseLeave={() => setHoverGroup(null)}
                  shape={(p) => (
                    <PointShape {...p}
                      dimmed={hoverGroup != null && hoverGroup !== g.key}
                      focused={hoverGroup === g.key} />
                  )}
                />
              ))}
            </ScatterChart>
          </ResponsiveContainer>
          <div className="sh-caption">
            Hover any point or legend entry for detail &middot; thick green ring = clears both
            hurdles &middot; small faded dot = level-2 supersector
          </div>
        </div>

        <div>
          <div className="sh-swhead">{active.mode === 'region' ? (active.code === CTRY_TAB ? 'Country' : 'Region') : 'Sector'}</div>
          <div className="sh-swatches">
            {groups.map((g) => (
              <span key={g.key}
                    className={`sh-sw${g.level === 2 ? ' l2' : ''}${g.opp && g.rows.length === 1 ? ' opp' : ''}`
                      + (hoverGroup && hoverGroup !== g.key ? ' dim' : '')}
                    onMouseEnter={() => setHoverGroup(g.key)}
                    onMouseLeave={() => setHoverGroup(null)}>
                <i style={{ background: g.colour }} />
                <span>{g.name}{g.rows.length > 1 ? ` (${g.rows.length})` : ''}</span>
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className="sh-tablewrap">
        <table className="sh-table">
          <thead>
            <tr>
              {TABLE_COLS.map(([key, label]) => {
                if (key === 'sector') {
                  return (
                    <th key={key} className="sh-th-text"
                        onClick={() => setSort({ key, asc: sort.key === key ? !sort.asc : true })}>
                      {active.code === CTRY_TAB ? 'Country' : 'Sector'}
                    </th>
                  );
                }
                return (
                  <th key={key} title={COL_HELP[key] || ''}
                      onClick={() => setSort({ key, asc: sort.key === key ? !sort.asc : false })}>
                    {label}{sort.key === key ? (sort.asc ? ' ▲' : ' ▼') : ''}
                  </th>
                );
              })}
              {active.showRegion && <th className="sh-th-text">Region</th>}
            </tr>
          </thead>
          <tbody>
            {sorted.map((r) => (
              <tr key={r.ticker} className={`${r.level === 2 ? 'sh-l2' : ''} ${r.opp ? 'sh-opp' : ''}`}>
                <td className="sh-td-text"
                    title={`${r.ticker} — ${int(r.hist_years)}y of history`}>{label(r)}</td>
                {TABLE_COLS.slice(1).map(([key, , kind]) => {
                  const v = r[key];
                  const style = kind === 'pct' ? { background: growthBg(v) }
                    : kind === 'pctile' ? { background: pctileBg(v) }
                    : kind === 'score' && v != null
                      ? { background: `rgba(7,32,64,${(v / 100 * 0.5).toFixed(3)})`,
                          color: v > 65 ? '#fff' : undefined, fontWeight: 600 }
                      : undefined;
                  const text = kind === 'pct' ? pct(v)
                    : kind === 'num' ? num(v)
                    : kind === 'text' ? v : int(v);
                  return <td key={key} style={style}>{text}</td>;
                })}
                {active.showRegion && (
                  <td className="sh-td-text">
                    <i className="sh-dot" style={{ background: REGION_COLOURS[r.region] }} />
                    {r.regionName}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="sh-foot">
        <b>Source:</b> LSEG Datastream, Drummond Capital Partners. Momentum and current
        levels from the daily panel; valuation percentiles from monthly history back to 2000.
        A trailing PE of exactly zero is Datastream&apos;s &ldquo;not meaningful&rdquo; marker for
        loss-making sectors and is treated as a gap, so a few thin sectors show blanks.
        Returns are price returns in local currency, not total returns. Percentiles are
        backward-looking and say nothing about whether a re-rating is deserved.
      </div>
    </div>
  );
}
